package app.utils

import app.model.Company
import app.model.ProjectVariableValue
import app.model.RolloutExecution
import app.model.Stage
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

fun getDarkStatusBackgroundColor(
    latestRolloutExecution: RolloutExecution?
): String {
    val fallback = "bg-gray-100 dark:bg-gray-700"
    return when {
        latestRolloutExecution == null -> fallback
        latestRolloutExecution.status == "successful" -> "bg-green-500 dark:bg-green-700"
        latestRolloutExecution.status == "failed" -> "bg-red-500 dark:bg-red-700"
        latestRolloutExecution.status == "in_progress" -> "bg-blue-500 dark:bg-blue-700"
        else -> fallback
    }
}

fun getLightStatusBackgroundColor(
    latestRolloutExecution: RolloutExecution?
): String {
    val fallback = "bg-gray-100 dark:bg-gray-700"
    return when {
        latestRolloutExecution == null -> fallback
        latestRolloutExecution.status == "successful" -> "bg-green-100 dark:bg-green-800/30"
        latestRolloutExecution.status == "failed" -> "bg-red-100 dark:bg-red-800/30"
        latestRolloutExecution.status == "in_progress" -> "bg-blue-100 dark:bg-blue-800/30"
        else -> fallback
    }
}

fun formatDateTime(instant: Instant?): String {
    return instant?.toLocalDateTime(TimeZone.currentSystemDefault())
        ?.toString()
        ?.take(16)
        ?.replace('T', ' ')
        ?: "-"
}
/**
 * Creates a list of available scope options for a select dropdown.
 * Filters out scopes that are already in use by other values, except for the current scope.
 * Returns options in order: stage-only, company-only, and company-stage combinations.
 */
data class ScopeOption(val label: String, val value: ProjectVariableValue)

fun getScopeListForSelect(
    companies: List<Company>,
    stages: List<Stage>,
    currentValues: List<ProjectVariableValue>,
    currentScope: ProjectVariableValue
): List<ScopeOption> {
    val options = mutableListOf<ScopeOption>()

    fun isScopeUsed(companyId: Int?, stageId: Int?) = currentValues.any {
        it.companyId == companyId && it.stageId == stageId &&
                !(it.companyId == currentScope.companyId && it.stageId == currentScope.stageId)
    }

    stages.forEach { stage ->
        if (!isScopeUsed(null, stage.id)) {
            options.add(ScopeOption(stage.name, ProjectVariableValue(null, stage.id, "")))
        }
    }

    companies.forEach { company ->
        if (!isScopeUsed(company.id, null)) {
            options.add(ScopeOption(company.name, ProjectVariableValue(company.id, null, "")))
        }
    }

    companies.forEach { company ->
        stages.forEach { stage ->
            if (!isScopeUsed(company.id, stage.id)) {
                options.add(
                    ScopeOption(
                        "${company.name} - ${stage.name}",
                        ProjectVariableValue(company.id, stage.id, "")
                    )
                )
            }
        }
    }

    return options
}

/**
 * Finds the first available unused scope from all possible combinations.
 * Checks in order: stage-only, company-only, and company-stage combinations.
 * Returns null if all possible scopes are already in use.
 * Used for the + Button in the edit view to disable it, when every scope is already in use.
 */
fun findFirstAvailableScope(
    companies: List<Company>,
    stages: List<Stage>,
    currentValues: List<ProjectVariableValue>
): ProjectVariableValue? {
    fun isScopeUsed(companyId: Int?, stageId: Int?) = currentValues.any {
        it.companyId == companyId && it.stageId == stageId
    }

    stages.forEach { stage ->
        if (!isScopeUsed(null, stage.id)) {
            return ProjectVariableValue(null, stage.id, "")
        }
    }

    companies.forEach { company ->
        if (!isScopeUsed(company.id, null)) {
            return ProjectVariableValue(company.id, null, "")
        }
    }

    companies.forEach { company ->
        stages.forEach { stage ->
            if (!isScopeUsed(company.id, stage.id)) {
                return ProjectVariableValue(company.id, stage.id, "")
            }
        }
    }

    return null
}

/**
 * Converts a ProjectVariableValue's scope into a human-readable string.
 * Handles four cases:
 * - default (no company, no stage)
 * - stage-only (stage without company)
 * - company-only (company without stage)
 * - company-stage combination
 *
 * Should normally never show "ERROR" or someone used the wrong Stage/CompanyId in Swagger or
 * the Stage/Company got deleted. We currently don't check for wrong Company/StageId when inserting new ProjectVariables
 */
fun getScope(projectVariableValue: ProjectVariableValue, companies: List<Company>, stages: List<Stage>) = when {
    projectVariableValue.stageId == null && projectVariableValue.companyId == null -> "default"
    projectVariableValue.stageId != null && projectVariableValue.companyId == null ->
        stages.find { it.id == projectVariableValue.stageId }?.name ?: "ERROR"

    projectVariableValue.companyId != null && projectVariableValue.stageId == null ->
        companies.find { it.id == projectVariableValue.companyId }?.name ?: "ERROR"

    else -> {
        val stageName = stages.find { it.id == projectVariableValue.stageId }?.name
        val companyName = companies.find { it.id == projectVariableValue.companyId }?.name
        if (stageName != null && companyName != null) "$stageName - $companyName" else "ERROR"
    }
}
